<template>
  <div class="Erbium">

    <!-- Masthead -->
    <ProjectMasthead
      title="Erbium"
      subtitle="Online Event Registration Platform."
      srcImage="/assets/digital/erbium/mast-preview.png"
      largeImage="/assets/digital/erbium/mast-preview@2x.png"
      bgColour="#0093D0" />

    <!-- Testimonial -->
    <Testimonial
      shortQuote="Mike’s expertise and input on this project was invaluable."
      attributed="Mark Strongman, President and Founder, Erbium"
    >
      <template slot="fullQuote">
        <p>"We’ve been working with Mike Cassie the past 11 years, leveraging his design services on a variety of projects. Recently we brought him on board to help with the Product and UX/UI Design for a new SaaS platform we are developing. Mike’s expertise and input on this project was invaluable. He helped us define and translate user and business requirements into elegant UX solutions for very complex workflows and was able to easily interface with everyone within our company, from our executive stakeholders to our development team. We are excited by the impending launch of our new platform and would heartily recommend Mike’s services to other organizations looking to build world class products."</p>
      </template>
    </Testimonial>

    <!-- Project Details -->
    <div class="details">

      <!-- Intro -->
      <ContentSection title="Introduction">
        <p>I was hired to help a provider in the online registration space redesign their existing platform.</p>

        <p>The new platform would continue to provide online event registration, featuring robust tools including ecommerce functionality for paid events, multi-day/multi-session events, integrated email and website marketing, surveys, and integration with accommodation providers and accommodation booking, as well as a host of other tools.</p>
      </ContentSection>

      <!-- Role -->
      <ContentSection title="My Role">
        <h3>Product Design</h3>
        <p>Working with the company’s founders, I helped plan a vision for the product and how it would integrate such a diverse and robust toolset.</p>

        <h3>Identity Design</h3>
        <p>I developed their logo, visual and brand standards.</p>

        <h3>UX/UI Design &amp; Research</h3>
        <p>I was responsible for crafting the project’s UX and UI. This included research, generating journey flows, IA, prototypes and visual design of varying fidelities (from wires to interactive, in-browser production-ready prototypes in VueJS).</p>
      </ContentSection>

      <!-- Research -->
      <ContentSection title="Research &amp; Insights">
        <p>This was a space I had considerable experience in, having worked with the business previously on other projects so my knowledge of the landscape was fairly deep. For extending that knowledge to understanding user’s needs, I was able to rely on a plethora of information gathered on the previous system, documented user complaints and requests via conversations and support tickets. Further interviews were done with existing clients to explore the documented needs and pain points more deeply and we distilled those needs in a top level features list.</p>

        <p>I then did a competitive analysis of several competing platforms, signing up for and using these systems and then compared those platforms to the features in our existing platform.</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/competitive-analysis-900.png"
          largeImage="/assets/digital/erbium/competitive-analysis-1800.png"
          caption="Competitive analysis" />

        <p>As well, the existing platform was a full-service platform, but the new one would also offer self-service accounts in a traditional SaaS model. As such, we had to better understand that demographic and its needs and so conducted interviews with a small group of event planners. Out of this came our three key personas:</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/personas-900.png"
          largeImage="/assets/digital/erbium/personas-1800.png"
          caption="Personas study" />

        <p>I then did journey maps for two of those key personas to better understand their path setting up an event – the most painstaking and involved part of the process.</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/journey-map-900.png"
          largeImage="/assets/digital/erbium/journey-map-1800.png"
          :canZoom="true"
          caption="User journey map" />

        <p>After this initial research, we were left with these core take-aways:</p>

        <ol>
          <li>
            <h3>Registration Is More Than Just A Page</h3>
            <p><strong>Challenge:</strong> The biggest missing in the existing platform was the lack of tools to produce related event and marketing materials; namely a website and emails. We needed a simple-to-use but robust UI to allow users to build and maintain these supporting materials.</p>

            <p><strong>Opportunity:</strong> In our competitive analysis we saw evidence of these kinds of tools in other platforms. However, we felt the level of polish and easy-of-use in many of them was lacking, especially when compared to purpose-build website and email marketing platforms like SquareSpace, Wix or MailChimp. That level of polish in these toolsets was rare in event-specific platforms and delivering on that front would be a big differentiator, especially if the experience worked well on mobile as well (often a falling off point in some competitor systems).</p>
          </li>

          <li>
            <h3>As Goes Registration, So Goes the Website</h3>
            <p><strong>Challenge:</strong> The implementation of the website and email building tools would mean some degree of learning curve for users, which would be mitigated by an onboarding tutorial and supporting knowledgebase. It wasn’t a steep curve and we felt the power it put in users' hands would be worth the relative effort to become comfortable with it. That said, we were cognizant of the fact that our users still had to build the actual registration page, which can be notoriously complicated flows for bigger events. How to avoid yet another platform learning curve?</p>

            <p><strong>Opportunity:</strong> After iterating through numerous approaches, the ‘aha’ moment came when we realized we could use the exact same approach as building the website – and in fact we ended up treating the registration page as just another website page, whose layout and configuration worked using the same fundamental patterns – just with some different building blocks.</p>
          </li>

          <li>
            <h3>No Two Planners Are Alike</h3>
            <p><strong>Challenge:</strong> We already had insights from our existing system about the fact the registration process and needs vary widely in the event planning industry. Things like conditional logic in data gathering, role-specific data, varied pricing and attendance needs meant building a registration flow could be exceedingly complex. In the existing full-service model this was less an issue because the internal administrator toolset could be weighty and complex as it was only used by the business on behalf of customers. In the new platform however, we had to expose that level of customization to end users of varying technical know-how.</p>

            <p><strong>Opportunity:</strong> The opportunity here was akin to the website building tools; design and execute at a high level and the platform would be well ahead of many competitor toolsets. It was a daunting task but one I relished – and one that ultimately integrated very well within the entire framework for building websites, emails and registration pages.</p>
          </li>
        </ol>
      </ContentSection>

      <!-- Design -->
      <ContentSection title="Design">
        <p>I began the design process by working on the company’s identity. This included developing their logo and visual and brand guidelines. Identity options were tested against their existing client base.</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/branding-900.png"
          largeImage="/assets/digital/erbium/branding-1800.png" />

        <p>Typefaces were divided between marketing and the applicaiton interface. Marketing would leverage the primary brand typeface while the administraiton UI would focus on a simpler combinaiton of webfonts, which could be easily changed as needed for white-labelling.</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/typefaces-900.png"
          largeImage="/assets/digital/erbium/typefaces-1800.png" />

        <p>The colour palette for the application was also kept small and simple due to the fact that the application could be white-labelled for other brands. Only the brand's primary colour was used. All other interface colours were kept to neutrals so that the one primary brand colour could be swapped out for any other brand colour in order to easily re-brand for white-labelling.</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/colours-900.png"
          largeImage="/assets/digital/erbium/colours-1800.png" />

        <p>The project then continued in a highly agile and iterative process. Much of the initial UX design work was done in sketch format in collaborative working sessions with key stakeholders in the company (from executives, sales, and development team members).</p>

        <p>Each of these sessions would address the functionality for an upcoming sprint. Once roughed out on paper and/or whiteboard, I then moved onto refining and documenting these sessions in wires, flows diagrams and functional specifications.</p>
        
        <p>After further review and iteration, I tackled the final UX, UI and motion design in-browser, creating high fidelity prototypes in VueJS.</p>

        <ProjectImage
          srcImage="/assets/digital/erbium/sketch-flow-900.jpg"
          largeImage="/assets/digital/erbium/sketch-flow-1800.jpg"
          :canZoom="true"
          caption="Working out rules and filters UI" />

        <ProjectImageLaptop
          srcImage="/assets/digital/erbium/dashboard-690.png"
          largeImage="/assets/digital/erbium/dashboard-1800.png"
          :canZoom="true"
          caption="Admin dashboard" />

        <ProjectImageLaptop
          srcImage="/assets/digital/erbium/list-view-690.png"
          largeImage="/assets/digital/erbium/list-view-1800.png"
          :canZoom="true"
          caption="Admin list of events" />

        <ProjectImageLaptop
          srcImage="/assets/digital/erbium/branding-desktop-690.png"
          largeImage="/assets/digital/erbium/branding-desktop-1800.png"
          :canZoom="true"
          :canScroll="false"
          caption="Admin building event" />

          <ProjectImageMobile
          srcImage1="/assets/digital/erbium/add-widget-660"
          caption1="Buidling registration page from the widget library"
          :isVideo1="true"
          :center="true"
          :showTab="false" />

          <ProjectImageMobile
          srcImage1="/assets/digital/erbium/categories-660"
          caption1="Configuring properties on a widget"
          :isVideo1="true"
          :center="true"
          :showTab="false" />

          <ProjectImageMobile
          srcImage1="/assets/digital/erbium/register-660"
          caption1="Registering for an event"
          :isVideo1="true"
          :center="true"
          :showTab="false" />

        <p>Those final prototypes were then user-tested and improvements made until they were ready for the development team to integrate into their production cycle.</p>

      </ContentSection>

      <!-- Outcome -->
      <ContentSection title="Outcome &amp; Learnings">
        <p>The platform has soft-launched with some existing Fortune 500 clients and feedback has been extremely positive, with reactions comparing the platform as "Wix for registration!". That has resulted in a 2x to 3x growth in registration volume with these clients during the soft-launch period.</p>
      </ContentSection>      

    </div>

    <Pagination
      prevLabel="Hyatt"
      nextLabel="Cadex"
      prevRoute="Hyatt"
      nextRoute="Cadex" />

    <Footer />
  </div>
</template>

<script>
import ProjectMasthead from '@/components/ProjectMasthead.vue'
import Footer from '@/components/Footer.vue'
import Pagination from '@/components/Pagination.vue'
import Testimonial from '@/components/Testimonial.vue'
import ContentSection from '@/components/ContentSection.vue'
import ProjectImage from '@/components/ProjectImage.vue'
import ProjectImageMobile from '@/components/ProjectImageMobile.vue'
import ProjectImageLaptop from '@/components/ProjectImageLaptop.vue'

export default {
  name: 'Erbium',
  components: {
    ProjectMasthead,
    Footer,
    Pagination,
    Testimonial,
    ContentSection,
    ProjectImage,
    ProjectImageMobile,
    ProjectImageLaptop
  },
}
</script>

<style lang="scss" scoped>
  .Erbium {
    background: #fff;
  }
</style>
